import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUtensils, faCalendarDay, faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import './AppProfileModal.css';

// Helper function to format dates
const formatShortDate = d => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : "N/A";
const formatTime = d => d ? new Date(d).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : "N/A";

// Nutrition data extraction helpers
const findNutrientValue = (nutrients, name) => {
    const nutrient = nutrients?.find(n => n.nutrient === name);
    return nutrient ? nutrient.amount : 0;
};

const extractMacros = (nutrients) => {
    if (!nutrients) return { calories: 0, protein: 0, carbs: 0, fat: 0 };
    
    return {
        calories: findNutrientValue(nutrients, 'Energy'),
        protein: findNutrientValue(nutrients, 'Protein'),
        carbs: findNutrientValue(nutrients, 'Carbohydrate, by difference'),
        fat: findNutrientValue(nutrients, 'Total lipid (fat)')
    };
};

// Get meal type icon 
const getMealIcon = (type) => {
    const lowerType = (type || '').toLowerCase();
    if (lowerType.includes('breakfast')) return '🍳';
    if (lowerType.includes('lunch')) return '🥗';
    if (lowerType.includes('dinner')) return '🍽️';
    if (lowerType.includes('snack')) return '🥨';
    return '🍴';
};

// Macro nutrient ring component
const MacroRing = ({ value, max, color, label, unit, percentage }) => {
    const strokeWidth = 6;
    const radius = 50;
    const circumference = 2 * Math.PI * radius;
    const progress = Math.min(value / max, 1) * circumference;
    
    return (
        <div className="macro-ring-container">
            <svg className="macro-ring-svg" viewBox="0 0 110 110" preserveAspectRatio="xMidYMid meet">
                <circle 
                    cx="55" 
                    cy="55" 
                    r={radius} 
                    fill="none" 
                    stroke="#f0f0f0" 
                    strokeWidth={strokeWidth}
                />
                <circle 
                    cx="55" 
                    cy="55" 
                    r={radius} 
                    fill="none" 
                    stroke={color} 
                    strokeWidth={strokeWidth} 
                    strokeDasharray={circumference} 
                    strokeDashoffset={circumference - progress}
                    transform="rotate(-90 55 55)"
                    strokeLinecap="round"
                />
                <text 
                    x="55" 
                    y="55" 
                    textAnchor="middle" 
                    fontSize="16" 
                    fontWeight="bold" 
                    fill="#2d3748"
                >
                    {Math.round(value)}
                </text>
                <text 
                    x="55" 
                    y="75" 
                    textAnchor="middle" 
                    fontSize="12" 
                    fill="#718096"
                >
                    {unit}
                </text>
            </svg>
            <div className="macro-label">
                <span>{label}</span>
                {percentage && <span className="macro-percent">{Math.round(value / max * 100)}%</span>}
            </div>
        </div>
    );
};

const NutritionDailyView = ({ healthData }) => {
    const [selectedDate, setSelectedDate] = useState(null);
    const [availableDates, setAvailableDates] = useState([]);
    const [mealsForDay, setMealsForDay] = useState([]);
    const [dayNutrients, setDayNutrients] = useState(null);
    
    // Extract all available dates from nutrition data and reset selected date when healthData changes
    useEffect(() => {
        if (healthData) {
            const mealDates = new Set();
            
            // Get dates from meals
            healthData.meals?.forEach(meal => {
                const date = meal.time?.split('T')[0];
                if (date) mealDates.add(date);
            });
            
            // Get dates from macro summaries
            healthData.macros?.dailySummary?.forEach(day => {
                if (day.date) mealDates.add(day.date);
            });
            
            const sortedDates = Array.from(mealDates).sort((a, b) => new Date(b) - new Date(a));
            setAvailableDates(sortedDates);
            
            // Always reset to most recent date when healthData changes (user ID changes)
            // This prevents showing data from the wrong date when switching between users
            if (sortedDates.length > 0) {
                setSelectedDate(sortedDates[0]);
            } else {
                // Clear selected date if no dates are available
                setSelectedDate(null);
            }
        }
    }, [healthData]);
    
    // When selected date changes, update meals and nutrients
    useEffect(() => {
        if (!selectedDate || !healthData) return;
        
        // Get meals for selected date
        const meals = healthData.meals?.filter(meal => {
            const mealDate = meal.time?.split('T')[0];
            return mealDate === selectedDate;
        }) || [];
        
        setMealsForDay(meals);
        
        // Get day's nutrients
        const dayData = healthData.macros?.dailySummary?.find(day => day.date === selectedDate);
        setDayNutrients(dayData?.nutrients || []);
        
    }, [selectedDate, healthData]);
    
    // Change selected date
    const changeDate = (direction) => {
        const currentIndex = availableDates.indexOf(selectedDate);
        if (currentIndex === -1) return;
        
        let newIndex;
        if (direction === 'next') {
            newIndex = Math.max(currentIndex - 1, 0); // Moving back in the array goes to newer dates
        } else {
            newIndex = Math.min(currentIndex + 1, availableDates.length - 1);
        }
        
        setSelectedDate(availableDates[newIndex]);
    };
    
    // Calculate day's total macros
    const dayMacros = extractMacros(dayNutrients);
    
    // Calculate recommended daily values for comparison
    const recommendedValues = {
        calories: 2000, // Standard 2000 calorie diet
        protein: 50,    // 50g protein (approx)
        carbs: 275,     // 275g carbs (approx)
        fat: 78         // 78g fat (approx 35% of calories)
    };
    
    // Early return if no health data is available at all
    if (!healthData || (!healthData.meals?.length && !healthData.macros?.dailySummary?.length)) {
        return <div className="APM-no-data"></div>;
    }
    
    return (
        <div className="nutrition-daily-view">
            <div className="nutrition-date-selector">
                <button className="APM-month-nav-btn" onClick={() => changeDate('prev')}>
                    <FontAwesomeIcon icon={faChevronLeft} />
                </button>
                <div className="selected-date">
                    <FontAwesomeIcon icon={faCalendarDay} className="date-icon" />
                    <span>{selectedDate ? formatShortDate(selectedDate) : "No Date"}</span>
                </div>
                <button className="APM-month-nav-btn" onClick={() => changeDate('next')}>
                    <FontAwesomeIcon icon={faChevronRight} />
                </button>
            </div>
            
            <div className="nutrition-content">
                {/* Meals section (left) */}
                <div className="nutrition-meals-section">
                    <h3>Meals</h3>
                    
                    {mealsForDay.length === 0 ? (
                        <div className="APM-no-data"></div>
                    ) : (
                        <div className="meal-list">
                            {mealsForDay.map(meal => {
                                // Calculate meal nutrition
                                const mealMacros = {
                                    calories: 0,
                                    protein: 0,
                                    carbs: 0,
                                    fat: 0
                                };
                                
                                meal.items?.forEach(item => {
                                    const itemMacros = extractMacros(item.nutrients);
                                    mealMacros.calories += itemMacros.calories;
                                    mealMacros.protein += itemMacros.protein;
                                    mealMacros.carbs += itemMacros.carbs;
                                    mealMacros.fat += itemMacros.fat;
                                });
                                
                                return (
                                    <div key={meal.id} className="meal-card">
                                        <div className="meal-header">
                                            <div className="meal-title">
                                                <span className="meal-icon">{getMealIcon(meal.type)}</span>
                                                <span className="meal-type">{meal.type || 'Meal'}</span>
                                                <span className="meal-time">{formatTime(meal.time)}</span>
                                            </div>
                                            <div className="meal-calories">
                                                {Math.round(mealMacros.calories)} cal
                                            </div>
                                        </div>
                                        
                                        <div className="meal-macros">
                                            <div className="macro-pill">
                                                <span className="macro-value">{Math.round(mealMacros.protein)}g</span>
                                                <span className="macro-name">Protein</span>
                                            </div>
                                            <div className="macro-pill">
                                                <span className="macro-value">{Math.round(mealMacros.carbs)}g</span>
                                                <span className="macro-name">Carbs</span>
                                            </div>
                                            <div className="macro-pill">
                                                <span className="macro-value">{Math.round(mealMacros.fat)}g</span>
                                                <span className="macro-name">Fat</span>
                                            </div>
                                        </div>
                                        
                                        {meal.items?.length > 0 && (
                                            <div className="meal-items">
                                                {meal.items.map((item, idx) => {
                                                    const itemMacros = extractMacros(item.nutrients);
                                                    return (
                                                        <div key={idx} className="meal-item">
                                                            <div className="item-info">
                                                                <div className="APM-meal-item-name">{item.description}</div>
                                                                <div className="APM-meal-item-amount">{item.amount}g</div>
                                                            </div>
                                                            <div className="item-nutrition">
                                                                {Math.round(itemMacros.calories)} cal
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
                
                {/* Macros section (right) */}
                <div className="nutrition-macros-section">
                    <h3>Daily Nutrition</h3>
                    
                    {!dayNutrients ? (
                        <div className="no-data">No nutrition data for this date</div>
                    ) : (
                        <>
                            <div className="macros-container">
                                {/* Calories in a large ring on the left */}
                                <div className="main-macro">
                                    <MacroRing 
                                        value={dayMacros.calories}
                                        max={recommendedValues.calories}
                                        color="#90caf9"
                                        label="Calories"
                                        unit="kcal"
                                        percentage={true}
                                    />
                                </div>
                                
                                {/* Two rows of 4 rings each on the right */}
                                <div className="other-macros">
                                    {/* First row of 4 rings */}
                                    <div className="macro-row">
                                        <MacroRing 
                                            value={dayMacros.protein}
                                            max={recommendedValues.protein}
                                            color="#81c784"
                                            label="Protein"
                                            unit="g"
                                            percentage={true}
                                        />
                                        <MacroRing 
                                            value={dayMacros.carbs}
                                            max={recommendedValues.carbs}
                                            color="#ffb74d"
                                            label="Carbs"
                                            unit="g"
                                            percentage={true}
                                        />
                                        <MacroRing 
                                            value={dayMacros.fat}
                                            max={recommendedValues.fat}
                                            color="#e57373"
                                            label="Fat"
                                            unit="g"
                                            percentage={true}
                                        />
                                        <MacroRing 
                                            value={findNutrientValue(dayNutrients, 'Fiber, total dietary')}
                                            max={25} // Recommended daily fiber
                                            color="#ba68c8"
                                            label="Fiber"
                                            unit="g"
                                            percentage={true}
                                        />
                                    </div>
                                    
                                    {/* Second row of 4 rings */}
                                    <div className="macro-row">
                                        <MacroRing 
                                            value={findNutrientValue(dayNutrients, 'Sugars, Total')}
                                            max={30} // Recommended max sugar
                                            color="#f06292"
                                            label="Sugars"
                                            unit="g"
                                            percentage={true}
                                        />
                                        <MacroRing 
                                            value={findNutrientValue(dayNutrients, 'Sodium, Na')}
                                            max={2300} // Recommended max sodium
                                            color="#64b5f6"
                                            label="Sodium"
                                            unit="mg"
                                            percentage={true}
                                        />
                                        <MacroRing 
                                            value={findNutrientValue(dayNutrients, 'Calcium, Ca')}
                                            max={1000} // Recommended calcium
                                            color="#4dd0e1"
                                            label="Calcium"
                                            unit="mg"
                                            percentage={true}
                                        />
                                        <MacroRing 
                                            value={findNutrientValue(dayNutrients, 'Iron, Fe')}
                                            max={18} // Recommended iron
                                            color="#ff8a65"
                                            label="Iron"
                                            unit="mg"
                                            percentage={true}
                                        />
                                    </div>
                                </div>
                            </div>
                            
                            <div className="nutrition-details">
                                <h4>Additional Nutrients</h4>
                                <div className="nutrient-grid">
                                    <div className="nutrient-item">
                                        <span className="nutrient-name">Fiber</span>
                                        <span className="nutrient-value">
                                            {Math.round(findNutrientValue(dayNutrients, 'Fiber, total dietary'))}g
                                        </span>
                                    </div>
                                    <div className="nutrient-item">
                                        <span className="nutrient-name">Sugars</span>
                                        <span className="nutrient-value">
                                            {Math.round(findNutrientValue(dayNutrients, 'Sugars, Total'))}g
                                        </span>
                                    </div>
                                    <div className="nutrient-item">
                                        <span className="nutrient-name">Sodium</span>
                                        <span className="nutrient-value">
                                            {Math.round(findNutrientValue(dayNutrients, 'Sodium, Na'))}mg
                                        </span>
                                    </div>
                                    <div className="nutrient-item">
                                        <span className="nutrient-name">Calcium</span>
                                        <span className="nutrient-value">
                                            {Math.round(findNutrientValue(dayNutrients, 'Calcium, Ca'))}mg
                                        </span>
                                    </div>
                                    <div className="nutrient-item">
                                        <span className="nutrient-name">Iron</span>
                                        <span className="nutrient-value">
                                            {Math.round(findNutrientValue(dayNutrients, 'Iron, Fe'))}mg
                                        </span>
                                    </div>
                                    <div className="nutrient-item">
                                        <span className="nutrient-name">Potassium</span>
                                        <span className="nutrient-value">
                                            {Math.round(findNutrientValue(dayNutrients, 'Potassium, K'))}mg
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default NutritionDailyView;
